// Vendors
import { EmptyStatusComponent, FooterComponent } from '@openbank/ziniaweb-ui-components';
import React from 'react';
import { FormattedMessage } from 'react-intl';
// Components
import { ErrorModalComponent } from './components/modal/error-modal.component';
import { HeaderLayoutComponent } from 'components/header-layout/header-layout.component';
// Configurations
import {
  getButtonErrorLabel,
  getErrorDescription,
  getErrorImage,
  getErrorTitle,
  getFooterVariant,
} from './configurations/error.configurations';
// Constants
import {
  ERROR_COMPONENT_BUTTON_PROPS,
  ERROR_FOOTER_BUTTON_GHOST_PROPS,
  ERROR_COMPONENT_COMMON_PROPS,
} from './constants/error.constants';
// Hooks
import { useErrorHook } from './hooks/error.hook';
// Styles
import { ErrorProviderStyled } from './error.provider.styled';
import { HeaderLayoutContentFullHeightStyled } from 'styles/app.styled';
// Translations
import { CANCEL_BUTTON } from './translations/error.translations';
// Types
import { ErrorType } from './types/error.type';
// Utilities
import { convertErrorToErrorEnum, isOnboardingFlowError } from './utils/error.utils';

const ErrorProvider = ({ error }: ErrorType): React.ReactElement => {
  const mappedError = convertErrorToErrorEnum(error),
    {
      handleCloseCancelApplicationModal,
      handleManageErrorButtonClick,
      handleShowCancelApplicationModal,
      handleTrackErrorRetry,
      handleTrackCancelApplicationError,
      showModal,
      ...trackingHandlers
    } = useErrorHook(mappedError);

  return (
    <HeaderLayoutComponent>
      <HeaderLayoutContentFullHeightStyled>
        <ErrorProviderStyled>
          <EmptyStatusComponent
            {...ERROR_COMPONENT_COMMON_PROPS}
            description={<FormattedMessage id={getErrorDescription(mappedError)} />}
            image={getErrorImage(mappedError)}
            title={<FormattedMessage id={getErrorTitle(mappedError)} />}
          />
          <FooterComponent
            variant={getFooterVariant(mappedError)}
            mainButtonConfiguration={{
              ...ERROR_COMPONENT_BUTTON_PROPS,
              children: <FormattedMessage id={getButtonErrorLabel(mappedError)} />,
              onClick: () => {
                handleTrackErrorRetry();
                handleManageErrorButtonClick();
              },
            }}
            {...(isOnboardingFlowError(mappedError)
              ? {
                  buttonGhostConfiguration: {
                    ...ERROR_FOOTER_BUTTON_GHOST_PROPS,
                    children: <FormattedMessage id={CANCEL_BUTTON} />,
                    onClick: () => {
                      handleShowCancelApplicationModal();
                      handleTrackCancelApplicationError();
                    },
                  },
                }
              : {})}
          />
          <ErrorModalComponent
            {...{ showModal, ...trackingHandlers }}
            onClose={handleCloseCancelApplicationModal}
          />
        </ErrorProviderStyled>
      </HeaderLayoutContentFullHeightStyled>
    </HeaderLayoutComponent>
  );
};

export { ErrorProvider };
